import { useEffect, useState } from "react";
import type { SCMServiceInfo } from "~/components/pipeline/New/ConnectGitPicker/util/types";

// Tunable constant: show warning after this time
const SLOW_FETCH_THRESHOLD_MS = 5000;

interface UseRepositoryFetchWarningParams {
  loading: boolean;
  error: boolean;
  hasRepositories: boolean;
  selectedService: SCMServiceInfo | null;
}

interface UseRepositoryFetchWarningResult {
  showSlowWarning: boolean;
  showTimeoutWarning: boolean;
}

/**
 * Hook to detect slow or failed repository fetching and show appropriate warnings.
 *
 * This is a temporary stopgap solution to help users when repository fetching is slow or
 * times out. The entire hook can be deleted once we've optimised repository loading
 * and server side searching.
 *
 * @param loading - Whether repositories are currently being fetched
 * @param error - Whether the fetch request failed
 * @param hasRepositories - Whether any repositories have been loaded
 * @param selectedService - The currently selected SCM service
 * @returns Warning states
 */
export function useRepositoryFetchWarning({
  loading,
  error,
  hasRepositories,
  selectedService,
}: UseRepositoryFetchWarningParams): UseRepositoryFetchWarningResult {
  const [showSlowWarning, setShowSlowWarning] = useState(false);

  useEffect(() => {
    setShowSlowWarning(false);
  }, [selectedService?.uuid, loading]);

  // Start timer when loading begins
  useEffect(() => {
    // Don't show warning if:
    // - Not loading
    // - No service selected
    // - Already have repositories (cached)
    if (!loading || !selectedService || hasRepositories) {
      return;
    }

    // Timer to show warning after threshold
    const timerId = setTimeout(() => {
      // Only show warning if still loading with no repositories
      if (loading && !hasRepositories) {
        setShowSlowWarning(true);
      }
    }, SLOW_FETCH_THRESHOLD_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [loading, hasRepositories, selectedService]);

  const showTimeoutWarning = error && selectedService !== null;

  return {
    showSlowWarning,
    showTimeoutWarning,
  };
}
